import { useEffect, useState } from 'react';
import { getAllUsers, updateUserRole, updateUserStatus, grantPro, removePro, addAdminUser, getAllAdmins, removeAdminUser, adjustCredits, setCredits, repairUserCredits } from '../../services/admin';
import { useAuth, UserProfile } from '../../context/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CheckCircle, AlertCircle, ShieldAlert, Zap, X, CreditCard } from 'lucide-react';
import styles from './Admin.module.css';

export function AdminUsersManager() {
  const { profile, user } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [creditModal, setCreditModal] = useState<{ 
    user: UserProfile; 
    action: string; 
    delta?: number; 
    newCredits: number;
    reason: string;
  } | null>(null);

  const [proModal, setProModal] = useState<{
    user: UserProfile;
    duration: string;
    reason: string;
  } | null>(null);

  const [removeProModal, setRemoveProModal] = useState<{
    user: UserProfile;
    reason: string;
  } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = async () => {
    // No-op: Data is kept in sync in real-time via onSnapshot listeners in useEffect
  };

  useEffect(() => {
    setLoading(true);
    
    // Subscribe to users collection in real-time
    const unsubscribeUsers = onSnapshot(
      collection(db, 'users'), 
      (snapshot) => {
        const userData = snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile));
        setUsers(userData);
        setLoading(false);
      },
      (error) => {
        console.error("Failed to subscribe to users", error);
        setLoading(false);
      }
    );

    // Subscribe to admin_users collection in real-time
    const unsubscribeAdmins = onSnapshot(
      collection(db, 'admin_users'),
      (snapshot) => {
        const adminData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAdminUsers(adminData);
      },
      (error) => {
        console.error("Failed to subscribe to admin users", error);
      }
    );

    return () => {
      unsubscribeUsers();
      unsubscribeAdmins();
    };
  }, []);

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !newAdminEmail) return;
    if (profile.role !== 'super_admin') {
      alert("Only super admins can manually add admins.");
      return;
    }

    try {
      setIsAddingAdmin(true);
      await addAdminUser(profile.uid, newAdminEmail);
      alert(`Admin record created for ${newAdminEmail}. They will be granted admin access on their next login.`);
      setNewAdminEmail('');
      fetchData();
    } catch (error) {
      alert("Failed to add admin user.");
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (email: string) => {
    if (!profile) return;
    if (email === 'sahin.siddique@gmail.com') {
      alert("Super admin cannot be removed.");
      return;
    }
    if (window.confirm(`Revoke admin access for ${email}?`)) {
      await removeAdminUser(profile.uid, email);
      fetchData();
    }
  };

  const handleRoleChange = async (uid: string, newRole: any) => {
    if (!profile) return;
    if (profile.role !== 'super_admin') {
      alert("Only super admins can change roles.");
      return;
    }
    const user = users.find(u => u.uid === uid);
    if (!user) return;

    if (user.email === 'sahin.siddique@gmail.com') {
      alert("Super admin role cannot be changed.");
      return;
    }

    if (user.uid === profile.uid && newRole !== 'super_admin') {
      alert("You cannot remove your own super_admin role.");
      return;
    }

    if (window.confirm(`Change role for ${user.email || 'this user'} from ${user.role} to ${newRole}?`)) {
      try {
        await updateUserRole(
          { uid: profile.uid, email: profile.email || 'unknown-admin' },
          { uid: user.uid, email: user.email || user.uid || 'unknown-user' },
          newRole,
          user.role
        );
        fetchData();
        showToast(`Role updated for ${user.email || user.uid}`);
      } catch (error: any) {
        showToast(`Failed to update role: ${error.message}`, 'error');
      }
    }
  };

  const handleStatusChange = async (uid: string, action: string) => {
    if (!profile) return;
    const user = users.find(u => u.uid === uid);
    if (!user) return;

    // Safety check for super admins
    if (user.role === 'super_admin' && (action === 'suspended' || action === 'deleted')) {
      if (!window.confirm(`WARNING: ${user.email} is a SUPER ADMIN. Are you sure you want to ${action} this account?`)) {
        return;
      }
    }

    if (action === 'pro_grant') {
      setProModal({
        user,
        duration: '30',
        reason: ''
      });
      return;
    }

    if (action.startsWith('credits_')) {
      const currentCredits = user.credits || 0;
      let delta = 0;
      let newCredits = currentCredits;
      let actionLabel = '';

      if (action === 'credits_1') {
        delta = 1;
        newCredits = currentCredits + 1;
        actionLabel = 'Add 1 Credit';
      } else if (action === 'credits_3') {
        delta = 3;
        newCredits = currentCredits + 3;
        actionLabel = 'Add 3 Credits';
      } else if (action === 'credits_minus_1') {
        delta = -1;
        newCredits = Math.max(0, currentCredits - 1);
        actionLabel = 'Remove 1 Credit';
      } else if (action === 'credits_custom') {
        actionLabel = 'Set Custom Credits';
      }

      setCreditModal({
        user,
        action: actionLabel,
        delta: action === 'credits_custom' ? undefined : delta,
        newCredits: action === 'credits_custom' ? currentCredits : newCredits,
        reason: ''
      });
      return;
    }

    if (action === 'pro_remove') {
      setRemoveProModal({
        user,
        reason: ''
      });
      return;
    }

    if (action === 'active' || action === 'suspended' || action === 'deleted') {
      if (window.confirm(`Change status for ${user.email || 'this user'} to ${action}?`)) {
        try {
          await updateUserStatus(
            { uid: profile.uid, email: profile.email || 'unknown-admin' }, 
            { uid: user.uid, email: user.email || user.uid || 'unknown-user' }, 
            action, 
            user.status || 'active'
          );
          fetchData();
          showToast(`Status updated to ${action}`);
        } catch (error: any) {
          showToast(`Failed to update status: ${error.message}`, 'error');
        }
      }
      return;
    }
  };

  const handleRepairAllCredits = async () => {
    if (!profile || profile.role !== 'super_admin') return;
    if (!window.confirm("This will scan all users and normalize negative credit balances. Proceed?")) return;

    setLoading(true);
    let repairedCount = 0;
    try {
      for (const user of users) {
        const wasRepaired = await repairUserCredits({ uid: profile.uid, email: profile.email }, user);
        if (wasRepaired) repairedCount++;
      }
      alert(`Repair complete. ${repairedCount} user(s) normalized.`);
      fetchData();
    } catch (error) {
      alert("Failed to repair all credits.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCredits = async () => {
    if (!profile || !user || !creditModal || !creditModal.reason.trim()) return;

    if (!creditModal.user.uid) {
      showToast("Critical Error: Target user UID is missing.", "error");
      return;
    }

    try {
      const idToken = await user.getIdToken();
      if (!idToken) throw new Error("Authentication token missing. Please re-login.");

      if (creditModal.delta !== undefined) {
        await adjustCredits(
          idToken,
          creditModal.user.uid,
          creditModal.delta,
          creditModal.reason
        );
      } else {
        await setCredits(
          idToken,
          creditModal.user.uid,
          creditModal.newCredits,
          creditModal.user.credits || 0,
          creditModal.reason
        );
      }
      showToast(`Credits updated for ${creditModal.user.email}`);
      setCreditModal(null);
      fetchData();
    } catch (error: any) {
      showToast(`Failed to update credits: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleConfirmPro = async () => {
    if (!profile || !proModal || !proModal.reason.trim()) return;

    try {
      let expiryDate: Date | null = null;
      if (proModal.duration !== 'lifetime') {
        expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + parseInt(proModal.duration));
      }

      await grantPro(
        { uid: profile.uid, email: profile.email || 'unknown-admin' },
        { uid: proModal.user.uid, email: proModal.user.email || proModal.user.uid || 'unknown-user' },
        expiryDate,
        proModal.reason
      );

      showToast(`Pro access granted to ${proModal.user.email}`);
      setProModal(null);
      fetchData();
    } catch (error: any) {
      showToast(`Failed to grant Pro: ${error.message}`, 'error');
    }
  };

  const handleConfirmRemovePro = async () => {
    if (!profile || !removeProModal || !removeProModal.reason.trim()) return;

    try {
      await removePro(
        { uid: profile.uid, email: profile.email || 'unknown-admin' },
        { uid: removeProModal.user.uid, email: removeProModal.user.email || removeProModal.user.uid || 'unknown-user' },
        removeProModal.reason
      );

      showToast(`Pro access removed for ${removeProModal.user.email}`);
      setRemoveProModal(null);
      fetchData();
    } catch (error: any) {
      showToast(`Failed to remove Pro: ${error.message}`, 'error');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.email?.toLowerCase().includes(search.toLowerCase()) || u.displayName?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || u.status === statusFilter || (!u.status && statusFilter === 'active');
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  return (
    <div className={styles.adminPage}>
      <header className={styles.pageHeader}>
        <h1>User Management</h1>
      </header>

      {profile?.role === 'super_admin' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '24px', marginBottom: '24px' }}>
          <div className={styles.adminCard}>
            <h3>Add New Admin</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-3)', marginBottom: '16px' }}>
              Automatic Admin privileges on next login.
            </p>
            <form onSubmit={handleAddAdmin} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input 
                type="email" 
                placeholder="Admin Email" 
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                className={styles.searchInput}
                required
              />
              <button type="submit" className={styles.actionBtn} disabled={isAddingAdmin}>
                {isAddingAdmin ? 'Adding...' : 'Add Admin'}
              </button>
            </form>
          </div>

          <div className={styles.adminCard}>
            <h3>Admin Roster</h3>
            <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '12px' }}>
              <table className={styles.adminTable} style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map(admin => (
                    <tr key={admin.id}>
                      <td>{admin.email}</td>
                      <td>
                        <span className={styles.statusBadge} style={{ 
                          background: admin.status === 'active' ? 'rgba(57, 255, 20, 0.1)' : 'rgba(255, 68, 68, 0.1)',
                          color: admin.status === 'active' ? 'var(--accent)' : '#ff4444'
                        }}>
                          {admin.status}
                        </span>
                      </td>
                      <td>
                        {admin.email !== 'sahin.siddique@gmail.com' && admin.status === 'active' && (
                          <button 
                            onClick={() => handleRemoveAdmin(admin.email)}
                            style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <h2>Platform Users</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {profile?.role === 'super_admin' && (
              <button 
                onClick={handleRepairAllCredits}
                className={styles.actionBtn}
                style={{ padding: '8px 12px', fontSize: '0.75rem', background: 'rgba(57, 255, 20, 0.1)', color: 'var(--accent)', border: '1px solid var(--accent)' }}
              >
                Repair Credit Counters
              </button>
            )}
            <select className={styles.actionSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="deleted">Deleted</option>
            </select>
            <select className={styles.actionSelect} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
            <input 
              type="text" 
              placeholder="Search users..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
        
        {loading ? (
          <div style={{ padding: '24px' }}>Loading...</div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.adminTable}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Available</th>
                  <th>Used</th>
                  <th>Total Granted</th>
                  <th>Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => {
                  // Migration Fallback & Safe Calculation
                  // Definition: Available = current usable, Used = consumed, Total = available + used
                  const available = Math.max(0, user.credits || 0);
                  const used = user.reports_used || 0;
                  const total = available + used;
                  
                  return (
                    <tr key={user.uid}>
                      <td>
                        <div 
                          style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--accent)' }}
                          onClick={() => setSelectedUser(user)}
                        >
                          {user.displayName?.trim()
                            || (user as any).fullName?.trim()
                            || (user.email ? user.email.split('@')[0] : 'Unknown')}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{user.email}</div>
                      </td>
                      <td>
                        <select 
                          value={user.role} 
                          onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                          className={styles.actionSelect}
                          disabled={profile?.role !== 'super_admin' || user.email === 'sahin.siddique@gmail.com'}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[user.status || 'active']}`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td>
                        <span className={styles.statusBadge} style={{ 
                          background: available > 0 ? 'rgba(57, 255, 20, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                          color: available > 0 ? 'var(--accent)' : 'var(--text-3)'
                        }}>
                          {available}
                        </span>
                      </td>
                      <td>{used}</td>
                      <td>{total}</td>
                      <td>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-2)' }}>
                          Login: {user.lastLogin ? new Date(user.lastLogin.seconds * 1000).toLocaleDateString() : 'Never'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>
                          Created: {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td>
                        <select
                          value=""
                          onChange={(e) => handleStatusChange(user.uid, e.target.value)}
                          className={styles.actionSelect}
                        >
                          <option value="" disabled>Actions...</option>
                          <optgroup label="Account Status">
                            <option value="active">Reactivate User</option>
                            <option value="suspended" disabled={user.email === 'sahin.siddique@gmail.com'}>Suspend User</option>
                            <option value="deleted" disabled={user.email === 'sahin.siddique@gmail.com'}>Soft Delete User</option>
                          </optgroup>
                          <optgroup label="Credit Management">
                            <option value="credits_1">Add 1 Credit</option>
                            <option value="credits_3">Add 3 Credits</option>
                            <option value="credits_minus_1">Remove 1 Credit</option>
                            <option value="credits_custom">Set Custom Credits</option>
                          </optgroup>
                          <optgroup label="Pro Access">
                            {!user.isPro && <option value="pro_grant">Grant Pro</option>}
                            {user.isPro && <option value="pro_remove">Remove Pro</option>}
                          </optgroup>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {selectedUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-0)', padding: '32px', borderRadius: '12px', minWidth: '400px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>User Details</h2>
              <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <p><strong>Name:</strong> {selectedUser.displayName}</p>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p><strong>UID:</strong> {selectedUser.uid}</p>
              <p><strong>Plan:</strong> <span style={{ color: selectedUser.plan && selectedUser.plan !== 'free' ? 'var(--accent)' : 'inherit', fontWeight: selectedUser.plan && selectedUser.plan !== 'free' ? 700 : 400 }}>{selectedUser.plan || 'free'}</span></p>
              <p><strong>Payment Status:</strong> {selectedUser.paymentStatus || 'none'}</p>
              <p><strong>Credits:</strong> {selectedUser.credits || 0}</p>
              <p><strong>Reports Used:</strong> {selectedUser.reports_used || 0}</p>
              <p><strong>Remaining:</strong> {(selectedUser.credits || 0) - (selectedUser.reports_used || 0)}</p>
              {selectedUser.stripeCustomerId && <p><strong>Stripe Customer:</strong> {selectedUser.stripeCustomerId}</p>}
              {selectedUser.lastStripeSessionId && <p><strong>Last Session:</strong> {selectedUser.lastStripeSessionId}</p>}
              {selectedUser.proExpiresAt && (
                <p><strong>Pro Expires:</strong> {new Date(selectedUser.proExpiresAt.seconds * 1000).toLocaleDateString()}</p>
              )}
              <p><strong>Role:</strong> {selectedUser.role}</p>
              <p><strong>Status:</strong> {selectedUser.status || 'active'}</p>
              <p><strong>Joined:</strong> {selectedUser.createdAt ? new Date(selectedUser.createdAt.seconds * 1000).toLocaleString() : 'N/A'}</p>
              <p><strong>Last Login:</strong> {selectedUser.lastLogin ? new Date(selectedUser.lastLogin.seconds * 1000).toLocaleString() : 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Credit Modal */}
      <AnimatePresence>
        {creditModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(4px)' }}>
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{ background: 'var(--bg-1)', padding: '40px', borderRadius: '24px', minWidth: '450px', border: '1px solid var(--border)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: 'rgba(57, 255, 20, 0.1)', padding: '12px', borderRadius: '12px' }}>
                  <CreditCard className={styles.accentIcon} />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Confirm Credit Action</h2>
                  <p style={{ color: 'var(--text-3)', fontSize: '0.9rem' }}>Accountability reason required for all changes.</p>
                </div>
              </div>

              <div style={{ background: 'var(--bg-2)', padding: '20px', borderRadius: '16px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-3)' }}>Target User:</span>
                  <span style={{ fontWeight: 600 }}>{creditModal.user.email}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-3)' }}>Action:</span>
                  <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{creditModal.action}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                  <span style={{ color: 'var(--text-3)' }}>Current Credits:</span>
                  <span style={{ fontWeight: 600 }}>{creditModal.user.credits || 0}</span>
                </div>
                
                {creditModal.action === 'Set Custom Credits' ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-3)' }}>New Credit Value:</span>
                    <input 
                      type="number" 
                      min="0"
                      value={creditModal.newCredits}
                      onChange={(e) => setCreditModal({ ...creditModal, newCredits: parseInt(e.target.value) || 0 })}
                      style={{ width: '80px', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--accent)', background: 'var(--bg-1)', color: 'var(--text-1)', textAlign: 'center', fontWeight: 800 }}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-3)' }}>New Credits:</span>
                    <span style={{ color: 'var(--accent)', fontWeight: 800, fontSize: '1.1rem' }}>{creditModal.newCredits}</span>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-2)' }}>REASON FOR ACTION (REQUIRED)</label>
                <textarea 
                  placeholder="e.g. Manual correction after live payment test"
                  value={creditModal.reason}
                  onChange={(e) => setCreditModal({ ...creditModal, reason: e.target.value })}
                  style={{ width: '100%', minHeight: '100px', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-1)', resize: 'none', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <button 
                  onClick={() => setCreditModal(null)}
                  style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleConfirmCredits}
                  disabled={!creditModal.reason.trim()}
                  style={{ flex: 1.5, padding: '14px', borderRadius: '12px', border: 'none', background: creditModal.reason.trim() ? 'var(--accent)' : 'var(--bg-3)', color: '#000', cursor: creditModal.reason.trim() ? 'pointer' : 'not-allowed', fontWeight: 800 }}
                >
                  Confirm & Save
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Pro Grant Modal */}
      <AnimatePresence>
        {proModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(4px)' }}>
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{ background: 'var(--bg-1)', padding: '40px', borderRadius: '24px', minWidth: '450px', border: '1px solid var(--border)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: 'rgba(57, 255, 20, 0.1)', padding: '12px', borderRadius: '12px' }}>
                  <ShieldAlert className={styles.accentIcon} />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Grant Pro Access</h2>
                  <p style={{ color: 'var(--text-3)', fontSize: '0.9rem' }}>Enable unlimited reports for this user.</p>
                </div>
              </div>

              <div style={{ background: 'var(--bg-2)', padding: '20px', borderRadius: '16px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-3)' }}>Target User:</span>
                  <span style={{ fontWeight: 600 }}>{proModal.user.email}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                  <span style={{ color: 'var(--text-3)' }}>Pro Duration:</span>
                  <select 
                    value={proModal.duration}
                    onChange={(e) => setProModal({ ...proModal, duration: e.target.value })}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--accent)', background: 'var(--bg-1)', color: 'var(--text-1)', fontWeight: 600 }}
                  >
                    <option value="7">7 Days</option>
                    <option value="30">30 Days</option>
                    <option value="90">90 Days</option>
                    <option value="lifetime">Lifetime / No Expiry</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-2)' }}>REASON FOR GRANT (REQUIRED)</label>
                <textarea 
                  placeholder="e.g. Internal testing or compensation"
                  value={proModal.reason}
                  onChange={(e) => setProModal({ ...proModal, reason: e.target.value })}
                  style={{ width: '100%', minHeight: '100px', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-1)', resize: 'none', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <button 
                  onClick={() => setProModal(null)}
                  style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleConfirmPro}
                  disabled={!proModal.reason.trim()}
                  style={{ flex: 1.5, padding: '14px', borderRadius: '12px', border: 'none', background: proModal.reason.trim() ? 'var(--accent)' : 'var(--bg-3)', color: '#000', cursor: proModal.reason.trim() ? 'pointer' : 'not-allowed', fontWeight: 800 }}
                >
                  Confirm Grant Pro
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Pro Remove Modal */}
      <AnimatePresence>
        {removeProModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(4px)' }}>
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{ background: 'var(--bg-1)', padding: '40px', borderRadius: '24px', minWidth: '450px', border: '1px solid var(--border)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '12px', borderRadius: '12px' }}>
                  <AlertCircle style={{ color: '#ef4444' }} />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Remove Pro Access</h2>
                  <p style={{ color: 'var(--text-3)', fontSize: '0.9rem' }}>User will return to credit-based system.</p>
                </div>
              </div>

              <div style={{ background: 'var(--bg-2)', padding: '20px', borderRadius: '16px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-3)' }}>Target User:</span>
                  <span style={{ fontWeight: 600 }}>{removeProModal.user.email}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                  <span style={{ color: 'var(--text-3)' }}>Current Plan:</span>
                  <span style={{ color: 'var(--accent)', fontWeight: 700 }}>PRO ACCESS</span>
                </div>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-2)' }}>REASON FOR REMOVAL (REQUIRED)</label>
                <textarea 
                  placeholder="e.g. End of trial period or account adjustment"
                  value={removeProModal.reason}
                  onChange={(e) => setRemoveProModal({ ...removeProModal, reason: e.target.value })}
                  style={{ width: '100%', minHeight: '100px', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-1)', resize: 'none', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <button 
                  onClick={() => setRemoveProModal(null)}
                  style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleConfirmRemovePro}
                  disabled={!removeProModal.reason.trim()}
                  style={{ flex: 1.5, padding: '14px', borderRadius: '12px', border: 'none', background: removeProModal.reason.trim() ? '#ef4444' : 'var(--bg-3)', color: '#fff', cursor: removeProModal.reason.trim() ? 'pointer' : 'not-allowed', fontWeight: 800 }}
                >
                  Confirm Removal
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            style={{ position: 'fixed', bottom: '40px', left: '50%', transform: 'translateX(-50%)', zIndex: 2000, display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 24px', borderRadius: '12px', background: toast.type === 'success' ? '#10b981' : '#ef4444', color: '#fff', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)' }}
          >
            {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span style={{ fontWeight: 600 }}>{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
